"use client";

import { useEffect, useRef, useState } from "react";
import styles from "./InlineReadMoreText.module.scss";
import { InlineReadMoreTextProps } from "./InlineReadMoreText.types";

export default function InlineReadMoreText({
  text,
  maxLines = 2,
  readMoreText = "Read more",
  readLessText = "see less",
  className = "",
  textClassName = "",
  readMoreClassName = "",
  readLessClassName = "",
  ellipsisClassName = "",
  style,
  truncateOnDesktop = false,
  showReadLess = true,
}: InlineReadMoreTextProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    setIsHydrated(true);

    const updateIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);

    return () => window.removeEventListener("resize", updateIsMobile);
  }, []);

  useEffect(() => {
    if (!isHydrated || !textRef.current) {
      setNeedsTruncation(false);
      return;
    }

    const shouldCheckTruncation = isMobile || truncateOnDesktop;
    if (!shouldCheckTruncation) {
      setNeedsTruncation(false);
      return;
    }

    const element = textRef.current;
    const updateTruncation = () => {
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
      const maxHeight = lineHeight * maxLines;
      const originalMaxHeight = element.style.maxHeight;

      element.style.maxHeight = "none";
      const fullHeight = element.scrollHeight;
      element.style.maxHeight = originalMaxHeight;

      setNeedsTruncation(fullHeight > maxHeight + 1);
    };

    updateTruncation();

    const observer = new ResizeObserver(updateTruncation);
    observer.observe(element);

    return () => observer.disconnect();
  }, [isHydrated, isMobile, maxLines, text, truncateOnDesktop]);

  const maxHeight = `${maxLines * 1.5}em`;
  const mobileMaxHeight = `${maxLines * 1.4}em`;
  const shouldCollapse = needsTruncation && !isExpanded;

  return (
    <div className={`${styles.wrapper} ${className}`} style={style}>
      <p
        ref={textRef}
        className={`${styles.text} ${textClassName} ${
          shouldCollapse ? styles.collapsed : styles.expanded
        }`}
        style={{
          "--max-height": maxHeight,
          "--mobile-max-height": mobileMaxHeight,
        } as React.CSSProperties}
      >
        {text}
        {shouldCollapse && (
          <>
            <span className={`${styles.ellipsis} ${ellipsisClassName}`}>...</span>
            <button
              type="button"
              className={`${styles.readMoreButton} ${readMoreClassName}`}
              onClick={() => setIsExpanded(true)}
            >
              {readMoreText}
            </button>
          </>
        )}
        {!shouldCollapse && isExpanded && showReadLess && (
          <>
            {" "}
            <button
              type="button"
              className={`${styles.readLessButton} ${readLessClassName}`}
              onClick={() => setIsExpanded(false)}
            >
              {readLessText}
            </button>
          </>
        )}
      </p>
    </div>
  );
}
